import { getFormattedDateFromInterval } from "#webapp/src/components/chat/util/getFormattedDateFromInterval";
import { RecurrenceRule } from "@clockwise/client-commons/src/datatypes/RecurrenceRule";
import { FlexStatus } from "@clockwise/schema/v2";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { Interval } from "luxon";
import { useMemo } from "react";
import { EventDetailsByIdQuery } from "../../hooks/__generated__/EventDetailsById.v2.generated";
import { useGatewayEventDetails } from "../../hooks/useGatewayEventDetails";
import { sortedAttendeesByOrganizerYouAndStatus } from "../utils/sortedAttendeesByOrganizerYouAndStatus";

type EventPopoverDetails = {
  attendees: EventDetailsAttendees;
  canMarkFlexible: boolean;
  description: string | undefined;
  eventTimeString: string | undefined;
  dateOrTimeRange: string | undefined;
  hasExternalAttendees: boolean;
  id: string | undefined;
  isFlexible: boolean;
  flexStatus: FlexStatus | undefined;
  isOrganizer: boolean;
  loading: boolean;
  ownerDisplayName: string;
  recurrenceString: string | undefined;
  recurrenceRule: string | undefined;
  refetch: () => void;
  isLocked: boolean;
  isHold: boolean;
  isMyEvent: boolean;
};

type EventDetails = EventDetailsByIdQuery["event"];
export type EventDetailsAttendees = Extract<EventDetails, { __typename: "Event" }>["attendees"];

export const useEventPopoverDetails = ({
  externalEventId,
  calendarId,
}: {
  externalEventId: string;
  calendarId: string;
}): EventPopoverDetails => {
  const { data, loading: loadingData, refetch } = useGatewayEventDetails(
    externalEventId,
    calendarId,
  );

  // `loadingData` will still be true if we hit the cache, so only show the loading spinner if we don't have any data.
  const loading = loadingData && !data;
  const isFlexible = !!data?.event?.flexibility?.isFlexible;
  const flexStatus = data?.event?.flexibility?.status;
  const canMarkFlexible = !!data?.event?.canMarkFlexible;

  const event = data?.event;
  const eventId = event?.id;
  const isOrganizer = event?.organizer?.person.isMe ?? false;
  const isHold = event?.isHold ?? false;
  const isMyEvent = event?.isMyEvent ?? false;
  const description = data?.event?.description || undefined;
  const recurrenceRule = data?.event?.recurrenceRule ?? undefined;
  const dateOrTimeRange =
    data?.event?.dateOrTimeRange.__typename === "DateTimeRange"
      ? data.event.dateOrTimeRange.timeRange
      : data?.event?.dateOrTimeRange.dateRange;

  const ownerDisplayName = event?.calendar.displayName ?? calendarId;

  // Attendee sorting matches event card attendee sorting

  const attendees = useMemo(
    () =>
      data?.event?.attendees
        ? sortedAttendeesByOrganizerYouAndStatus(data?.event?.attendees, calendarId)
        : [],
    [calendarId, data?.event?.attendees],
  );

  const hasExternalAttendees = attendees.some((attendee) => attendee.isExternal);

  const recurrenceString = useMemo(() => {
    if (data?.event?.recurrenceRule) {
      const startTime = Interval.fromISO(
        data?.event?.dateOrTimeRange.__typename === "DateRange"
          ? data?.event?.dateOrTimeRange.dateRange
          : data?.event?.dateOrTimeRange.timeRange,
      ).start;
      try {
        return new RecurrenceRule(data?.event?.recurrenceRule).toText({
          date: startTime.toISODate(),
          timezone: getRenderTimeZone(),
        });
      } catch (e) {
        return "";
      }
    } else {
      return "";
    }
  }, [data?.event?.recurrenceRule, data?.event?.dateOrTimeRange]);

  const eventTimeString = dateOrTimeRange
    ? getFormattedDateFromInterval(dateOrTimeRange, {
        showRelativeDay: false,
      })
    : undefined;

  const isLocked = !!data?.event?.isLocked;

  return useMemo(() => {
    return {
      attendees,
      canMarkFlexible,
      description,
      eventTimeString,
      dateOrTimeRange,
      hasExternalAttendees,
      id: eventId,
      isFlexible,
      flexStatus,
      isOrganizer,
      loading,
      ownerDisplayName,
      recurrenceString,
      recurrenceRule,
      isHold,
      isMyEvent,
      refetch,
      isLocked,
    };
  }, [
    attendees,
    canMarkFlexible,
    description,
    eventTimeString,
    dateOrTimeRange,
    eventId,
    hasExternalAttendees,
    isFlexible,
    flexStatus,
    isOrganizer,
    loading,
    ownerDisplayName,
    recurrenceString,
    recurrenceRule,
    isHold,
    isMyEvent,
    refetch,
    isLocked,
  ]);
};
